import { useState, useEffect } from 'react';
import { auth } from '../services/firebase';
import { User } from '../types';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  
  const updateUserProfile = async (uid: string, data: Partial<User>) => {
    try {
      const userRef = doc(db, 'users', uid);
      await setDoc(userRef, data, { merge: true });
      // After updating, fetch and set the latest user data
      const updatedDoc = await getDoc(userRef);
      if (updatedDoc.exists()) {
        setUser(updatedDoc.data() as User);
      }
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        // Fetch the full user profile from Firestore
        const userRef = doc(db, 'users', firebaseUser.uid);
        const userDoc = await getDoc(userRef);
        
        // Check if the emailVerified status needs to be updated
        if (userDoc.exists()) {
          const userData = userDoc.data() as User;
          if (userData.emailVerified !== firebaseUser.emailVerified) {
            // Update Firestore to match Firebase Auth's emailVerified status
            await setDoc(userRef, {
              emailVerified: firebaseUser.emailVerified
            }, { merge: true });
          }
          setUser({
            ...userData,
            emailVerified: firebaseUser.emailVerified // Make sure to use the latest value
          });
        } else {
          // If no profile exists yet, set basic user data
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            location: '',
            ethnicityPreference: '',
            displayName: '',
            age: '',
            gender: '',
            bio: '',
            interests: '',
            occupation: '',
            education: '',
            lookingFor: '',
            height: '',
            birthdate: '',
            genderPreference: '',
            additionalPhotos: [],
            profilePicture: '',
            verificationSelfie: '',
            matches: [],
            verified: false,
            verificationStatus: 'none',
            ethnicities: [],
            nationality: [],
            emailVerified: firebaseUser.emailVerified // Use Firebase Auth's value
          });
        }
      } else {
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  return { user, updateUserProfile };
};